import {XIcon} from '@heroicons/react/outline';
import type {ReactElement} from 'react';
import {useCallback, useState} from 'react';

import {getFeatureRuleValueError} from '@afriex/core/domain/entities/FeatureValidator';

import {Button, PlusIcon, Select} from '@/components';
import type {AfriexFeatureTagMap, AfriexFeatureTags} from '@/types';
import {AfriexFeatureField, BusinessFeeComparator} from '@/types';
import {formatSelectOptionItem} from '@/utils/merchant';
import {showErrorMessage} from '@/utils/showErrorMessage';

import {FeatureRuleValueInput} from './FeatureRuleValueInput';
type Props = {
  onChange: (value: AfriexFeatureTagMap) => void;
  tags?: AfriexFeatureTags;
  value?: string;
  onRemove: (tag: AfriexFeatureField) => void;
  fieldsToOmit: AfriexFeatureField[];
};

const mapFieldToComparators = (
  field: AfriexFeatureField,
): BusinessFeeComparator[] => {
  switch (field) {
    case AfriexFeatureField.SignupDate:
    case AfriexFeatureField.TransactionCount:
    case AfriexFeatureField.TransactionVolume:
      return [
        BusinessFeeComparator.Equals,
        BusinessFeeComparator.LessThan,
        BusinessFeeComparator.GreaterThan,
        BusinessFeeComparator.Between,
      ];

    default:
      return [BusinessFeeComparator.Equals];
  }
};
const mapOptionsToList = (options: string[], fieldsToOmit: string[] = []) =>
  options
    ?.filter((item) => !fieldsToOmit.includes(item))
    ?.map((item) => ({
      key: formatSelectOptionItem(item || ''),
      value: item || '',
    }));

export const FeaturePredicateInput = ({
  onChange,
  tags,
  onRemove,
  fieldsToOmit,
}: Props): ReactElement => {
  const predicateFieldOptions = mapOptionsToList(
    Object.values(AfriexFeatureField),
    fieldsToOmit,
  );
  const [selectedField, setSelectedField] = useState<AfriexFeatureField>(
    AfriexFeatureField.Country,
  );
  const [selectedComparator, setSelectedComparator] =
    useState<BusinessFeeComparator>(BusinessFeeComparator.Equals);

  const [selectedValue, setSelectedValue] = useState<string>('');
  const [filteredComparators, setFilteredComparators] = useState<
    BusinessFeeComparator[]
  >(() => mapFieldToComparators(selectedField));

  const comparatorOptions = mapOptionsToList(filteredComparators);

  const handleFieldChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const field = event.target.value as AfriexFeatureField;
    setSelectedField(field);
    const comparators = mapFieldToComparators(field);
    setFilteredComparators(comparators);
    setSelectedComparator(comparators[0]);
  };

  const handleComparatorChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedComparator(event.target.value as BusinessFeeComparator);
  };

  // Takes an empty value too, so Add follows what the input shows after it is
  // cleared or the field changes.
  const handleValueChange = (val: string) => {
    setSelectedValue(val);
  };

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e?.preventDefault();
      if (!selectedField || !selectedComparator || !selectedValue) {
        showErrorMessage('Please provide all required filter values');
        return;
      }
      // Explained here, with the inputs kept, rather than dropped by the reducer.
      const valueError = getFeatureRuleValueError(
        selectedField,
        selectedComparator,
        selectedValue,
      );
      if (valueError) {
        showErrorMessage(valueError);
        return;
      }
      const tag = {
        [selectedField]: `${selectedComparator}::${selectedValue}`,
      };

      // The value stays pending while it is still shown, so Add follows it.
      onChange(tag);
    },
    [selectedField, selectedComparator, selectedValue],
  );
  const tagList = Object.keys(tags ?? {});
  return (
    <div>
      <label
        htmlFor="price"
        className="block text-sm font-medium leading-6 text-gray-700 dark:text-gray-200"
      >
        Available to any user with:
      </label>
      <ul className="flex flex-row flex-wrap gap-2 mb-1">
        {(tagList || [])?.length > 0
          ? tagList?.map((tagKey) => (
              <li
                key={tagKey}
                data-cy="merchant-features-form-rule-chip"
                className="flex items-center gap-2 p-1 px-2 rounded bg-indigo-300 text-sm dark:bg-indigo-900 dark:text-white dark:hover:bg-indigo-800 cursor-pointer max-w-full"
              >
                {/* Only the label can be long, so it is the one allowed to
                    shrink and wrap; the icon keeps its size. */}
                <span className="min-w-0 break-words">
                  {`${tagKey} ${tags?.[tagKey as AfriexFeatureField]?.replace('::', ' ')}`}
                </span>
                <XIcon
                  data-cy="merchant-features-form-rule-chip-remove"
                  className="w-4 h-4 text-red-500 cursor-pointer shrink-0"
                  onClick={() => {
                    onRemove(tagKey as AfriexFeatureField);
                    return false;
                  }}
                />
              </li>
            ))
          : ''}
      </ul>
      <hr className="border-1 border-gray-200 dark:border-dark-borderMid mt-2 mb-4 w-full" />
      <div className="mt-1 rounded-md shadow-sm w-full flex gap-2">
        <Select
          id="field"
          name="field"
          dataCy="merchant-features-form-rule-field"
          className="w-full lg:w-fit"
          onChange={handleFieldChange}
          value={selectedField}
          options={predicateFieldOptions}
          placeholder="---Select One---"
        />
        <Select
          id="currency"
          name="currency"
          dataCy="merchant-features-form-rule-comparator"
          className="w-full lg:w-fit"
          onChange={handleComparatorChange}
          value={selectedComparator}
          options={comparatorOptions}
          placeholder="---Select One---"
        />
      </div>
      <div className="mt-2">
        <div className="flex flex-col gap-2">
          <div className="w-full lg:w-fit">
            <FeatureRuleValueInput
              field={selectedField}
              comparator={selectedComparator}
              handleValueChange={handleValueChange}
            />
          </div>
          <Button
            type="button"
            variant="solid"
            className="w-fit"
            dataCy="merchant-features-form-add-rule"
            disabled={!selectedField || !selectedComparator || !selectedValue}
            onClick={(e: React.FormEvent) => handleSubmit(e)}
          >
            Add <PlusIcon className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};
